import { Popover, Tooltip } from 'antd';
import { tileDataProp } from 'components/atoms/TileButton';
import Barchart from 'components/molecules/Barchart';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import HorizontalInfiniteScroller from 'components/molecules/HorizontalInfiniteScroller';
import Legend from 'components/molecules/Legend';
import Loader from 'components/molecules/Loader';
import { salesDataType } from 'components/pages/GrowthAnalysis';
import { growthAnalysisTableId, height, yAxisLabelClass } from 'components/template/GrowthAnalysis/ExpandedView';
import { colorConstants } from 'config/colorConstants';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import _ from 'lodash';
import React, { forwardRef, useContext, useEffect, useState } from 'react';
import {
    calculateFontSize,
    fetchCurrency,
    fetchUnit,
    formatNumWithKMB,
    formatNumWithKMBAlt,
    formatUnit,
    scaleMaxValue,
    ellipseMiddleString,
    scaleMinValue,
    getConfig,
} from 'utils/helperMethods';
import { trackEventMixpanel } from 'utils/mixpanel';
import { GROWTH_ANALYSIS_EXPLORE_MORE_CLICKED, GROWTH_ANALYSIS_LEFT_CLICKED_DRILLDOWN } from 'utils/mixpanelEventNames';
import { graphXAxisLabelFontSize } from '../BrandValueChartContainer/BrandValueChartContainer';
import { UserContext } from 'contexts/ConfigContext';
import { classToRemoveScrollbar } from 'components/molecules/MicroIntractions/CopyTOClipBoard';

const expandMoreSvg = (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M7.99967 14.6666C11.6816 14.6666 14.6663 11.6819 14.6663 7.99998C14.6663 4.31808 11.6816 1.33331 7.99967 1.33331C4.31778 1.33331 1.33301 4.31808 1.33301 7.99998C1.33301 11.6819 4.31778 14.6666 7.99967 14.6666Z"
            stroke="#44338A"
            stroke-linecap="round"
            stroke-linejoin="round"
        />
        <path
            d="M9.81441 5.51062C10.1401 5.40206 10.303 5.34777 10.4113 5.38639C10.5055 5.42 10.5797 5.49416 10.6133 5.5884C10.6519 5.69669 10.5976 5.85954 10.489 6.18524L9.49733 9.16033C9.46641 9.25309 9.45096 9.29947 9.42461 9.33799C9.40127 9.3721 9.3718 9.40158 9.33768 9.42491C9.29917 9.45126 9.25278 9.46672 9.16003 9.49764L6.18494 10.4893C5.85924 10.5979 5.69639 10.6522 5.58809 10.6136C5.49385 10.58 5.41969 10.5058 5.38609 10.4116C5.34747 10.3033 5.40175 10.1404 5.51032 9.81472L6.50201 6.83963C6.53293 6.74687 6.54839 6.70049 6.57474 6.66197C6.59808 6.62786 6.62755 6.59838 6.66167 6.57505C6.70018 6.5487 6.74656 6.53324 6.83932 6.50232L9.81441 5.51062Z"
            stroke="#44338A"
            stroke-linecap="round"
            stroke-linejoin="round"
        />
    </svg>
);

const expandMoreFillSvg = (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M8.00033 0.666687C3.95024 0.666687 0.666992 3.94993 0.666992 8.00002C0.666992 12.0501 3.95024 15.3334 8.00033 15.3334C12.0504 15.3334 15.3337 12.0501 15.3337 8.00002C15.3337 3.94993 12.0504 0.666687 8.00033 0.666687ZM10.4119 5.38628C10.3036 5.34766 10.1408 5.40194 9.81506 5.51051L6.83997 6.5022C6.74721 6.53312 6.70083 6.54858 6.66231 6.57493C6.6282 6.59827 6.59872 6.62774 6.57539 6.66186C6.54904 6.70037 6.53358 6.74675 6.50266 6.83951L5.51096 9.8146C5.4024 10.1403 5.34812 10.3031 5.38673 10.4114C5.42034 10.5057 5.4945 10.5798 5.58874 10.6135C5.69704 10.6521 5.85989 10.5978 6.18558 10.4892L9.16067 9.49753C9.25343 9.46661 9.29981 9.45115 9.33833 9.4248C9.37244 9.40146 9.40192 9.37199 9.42526 9.33787C9.4516 9.29935 9.46706 9.25298 9.49798 9.16022L10.4897 6.18513C10.5982 5.85943 10.6525 5.69658 10.6139 5.58828C10.5803 5.49404 10.5061 5.41988 10.4119 5.38628Z"
            fill="#44338A"
        />
    </svg>
);

interface Props {
    exploreMoreOnClick: (item: any) => void;
    drillDownOnClick: (item: any) => void;
    index: number;
    expandedItem: any;
    drilledDownItem: any;
    disableItemHover: boolean;
    disableDrillDownHover: boolean;
    dropdownItems: any[];
    dropdownClickHandler: (item: any, attrType: any, index: number) => void;
    attrTypeOptions: any;
    data: salesDataType;
    attributeTypeItem: any;
    absTile: tileDataProp;
    volTile: tileDataProp;
    infiniteScrollerNext: (index: number, width: number) => void;
    infiniteScrollSettings: any;
    isTableExpanded: boolean;
    disableRightClick: boolean;
    showTotals?: boolean;
}

export const legendData = [
    { label: 'Positive', color: colorConstants.limeGreen },
    { label: 'Negative', color: colorConstants.red },
];

export const yAxisLabelMapping: any = {
    vol: '',
    val: 'Sales',
    units: 'Units',
    profit: 'Profit',
    frontline: 'Frontline Sales',
    net: 'Net Sales',
};

const yAxisGraphWidth = calculateFontSize(window.innerWidth, 120);

export const salesGraphWidth = calculateFontSize(window.innerWidth, 90);
export const barWidth = calculateFontSize(window.innerWidth, 31);

const SalesChartContainer = forwardRef<HTMLDivElement, Props>((props, ref) => {
    const {
        disableDrillDownHover,
        exploreMoreOnClick,
        drillDownOnClick,
        index,
        expandedItem,
        disableItemHover,
        drilledDownItem,
        dropdownItems,
        dropdownClickHandler,
        attrTypeOptions,
        data,
        attributeTypeItem,
        absTile,
        volTile,
        infiniteScrollerNext,
        infiniteScrollSettings,
        isTableExpanded,
        disableRightClick,
        showTotals,
    } = props;
    // const SalesChartContainer = forwardRef<HTMLDivElement, Props>((props, ref) => {
    const [isItemsModalOpen, setIsItemsModalOpen] = useState<boolean | number>(false);
    const containerWidth = useContainerWidth({
        containerId: `growth-analysis-sales-infinite-scroll${index}`,
        buffer: yAxisGraphWidth + 34 * 2,
    }); //subtracting graph width and padding on 2 sides
    const { appConfig } = useContext(UserContext);
    const growthAnalysisConfig = getConfig('growthAnalysis', appConfig);
    const showTotal = growthAnalysisConfig?.showTotal;
    const commonConfig = getConfig('commonConfig', appConfig);
    const showFullLabel = commonConfig?.showFullLabel;
    const decimalForPercentage = commonConfig?.decimalForPercentage ?? 2;
    const decimalForNonPercentage = commonConfig?.decimalForNonPercentage ?? 2;
    const { chartData: salesData, loading, error, count } = data;
    const { width = [0, 0] } = infiniteScrollSettings;
    const graphViewContainerWidth = Math.max(
        salesData?.data ? salesData.data?.length * salesGraphWidth : 0,
        containerWidth,
    );
    const perGraphComponentWidth = graphViewContainerWidth / salesData?.data?.length || 0;

    const handleRightClick = (e: any, index: number) => {
        e.preventDefault();
        setIsItemsModalOpen(index);
    };
    const hide = () => {
        setIsItemsModalOpen(false);
    };

    const handleOpenChange = (newOpen: boolean) => {
        if (!newOpen) setIsItemsModalOpen(newOpen);
    };

    const infiniteScrollOnNext = (width: number, loading: boolean) => {
        if (loading) return;
        //  console.log('logging asd', width, loading);
        infiniteScrollerNext(index, width);
    };

    //listening to scroll
    useEffect(() => {
        // Table element
        const tableId = `${growthAnalysisTableId}-${index}`;
        const tableScrollableElement = document.getElementById(tableId)?.getElementsByClassName('ant-table-body')[0];
        // Graph element
        const graphElem = document.getElementById(`growth-analysis-sales-infinite-scroll${index}`);

        // Function to synchronize scrolling of graph and table
        function scrollFunction(event: any) {
            if (event.target === graphElem) {
                // If graph is scrolled, scroll the table
                tableScrollableElement?.scrollTo?.({
                    left: Number(graphElem?.scrollLeft) * 1, // Giving a buffer because widths of table and chart are different
                    behavior: 'auto',
                });
            } else {
                // If table is scrolled, scroll the graph
                graphElem?.scrollTo?.({
                    left: Number(tableScrollableElement?.scrollLeft) * 1,
                    behavior: 'auto',
                });
            }
        }

        if (salesData?.data?.length > 0) {
            // Add event listeners for both graph and table
            graphElem?.addEventListener?.('scroll', scrollFunction, { passive: true });
            tableScrollableElement?.addEventListener?.('scroll', scrollFunction, { passive: true });

            return () => {
                // Cleanup: remove event listeners
                graphElem?.removeEventListener?.('scroll', scrollFunction);
                tableScrollableElement?.removeEventListener?.('scroll', scrollFunction);
            };
        }
    }, [salesData, loading, isTableExpanded]);

    // IF ERROR RETURN ERROR MESSAGE
    if (error && !loading && !salesData) return <ErrorBox msg={String(error)} />;

    // IF LOADING RETURN LOADER
    if (loading)
        return (
            <div style={{ height, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Loader />
            </div>
        );

    const YAxisUnit: any = formatUnit(`${absTile.key}_${volTile.key}`, true, appConfig);
    const currency = volTile?.key === 'val' && absTile.key === 'abs' ? fetchCurrency(appConfig) : '';
    let YAxisResult: any =
        volTile.key === 'vol' && absTile.key === 'percentage'
            ? `${fetchUnit(appConfig, 'volume')} ${YAxisUnit}`
            : `${yAxisLabelMapping[volTile.key]} ${YAxisUnit}`;

    // ELSE RETURN COMPONENT
    return (
        <section ref={ref} style={{ minHeight: height }}>
            {/* CATEGORY SALES CHART COMPONENT */}

            {salesData && (
                <div className="mb-4 flex justify-between items-center">
                    <p className={`${yAxisLabelClass} pl-[40px] w-[220px]`}>Change in {YAxisResult}</p>
                    <Legend data={legendData} />
                    <p className="w-[120px] font-normal text-greyFont font-xxs">
                        {absTile.key === 'percentage' ? '* % scale in Y-axis from -100% to +200%' : ''}
                    </p>
                </div>
            )}

            <div id="growthAnalysisSalesChart" className="flex ml-1 w-full">
                {/* Y AXIS */}
                <div style={{ paddingLeft: 60, width: yAxisGraphWidth }}>
                    <Barchart
                        colorKey="color"
                        dataset={[]}
                        height={height}
                        loading={false}
                        chartContainer={`SalesY-${props.index}`}
                        fields={['category', 'sales']}
                        minBound={scaleMinValue(absTile.key === 'percentage' ? 40 : 105, salesData?.minVal)}
                        maxBound={scaleMaxValue(absTile.key === 'percentage' ? 40 : 80, salesData?.maxVal)}
                        width={yAxisGraphWidth}
                        YGrid={false}
                        padding={[0, 0, 0, 0]}
                        yAxisLabelFormatter={(val: any) => {
                            return formatNumWithKMB(val);
                        }}
                        xAxisConfig={false}
                    />
                </div>

                <HorizontalInfiniteScroller
                    id={`growth-analysis-sales-infinite-scroll${index}`}
                    hasMore={salesData?.data?.length - (showTotals ? 1 : 0) < count || false} //subtracting 1 to remove total value which is the first item
                    scrollThreshold={0.9}
                    toScrollWidth={width[index]}
                    onNext={(width: number) => {
                        infiniteScrollOnNext(width, loading); 
                    }}
                >
                    <div
                        id={`growth-analysis-sales-infinite-scroll${index}`}
                        className={`flex pb-[45px] ${classToRemoveScrollbar}`}
                        style={{ maxWidth: '100%', overflowX: 'auto' }}
                    >
                        {salesData &&
                            'data' in salesData &&
                            Array.isArray(salesData.data) &&
                            salesData.data.map((data: any, idx: number) => {
                                const item = [data];
                                const disabled = item[0]?.disableHover || disableItemHover || disableDrillDownHover;
                                const disabledBarExploreMore = item[0]?.disableHover || disableItemHover;

                                const isItemSelected =
                                    (expandedItem?.attributeType ===
                                        _.snakeCase(attributeTypeItem.selectedAttributeType) &&
                                        expandedItem?.attributeExId === item[0]?.category) ||
                                    (drilledDownItem?.attributeType ===
                                        _.snakeCase(attributeTypeItem.selectedAttributeType) &&
                                        drilledDownItem?.attributeExId === item[0]?.category);
                                //if idx===0 it means showing total, make total bar sticky so that it doesnt move with scroll
                                return (
                                    <div
                                        key={idx}
                                        style={{
                                            position: showTotal && idx === 0 ? 'sticky' : 'static',
                                            left: showTotal && idx === 0 ? 0 : 'auto',
                                            background: showTotal && idx === 0 ? 'white' : 'transparent',
                                            zIndex: showTotal && idx === 0 ? 10 : 'auto',
                                        }}
                                    >
                                        <Popover
                                            overlayStyle={{ padding: 0 }}
                                            trigger="click"
                                            open={idx === isItemsModalOpen}
                                            onOpenChange={handleOpenChange}
                                            placement={'right'}
                                            showArrow={false}
                                            overlayInnerStyle={{ position: 'relative', right: 20, padding: 0 }}
                                            content={
                                                <DropdownItems
                                                    item={item}
                                                    attrTypeOptions={attrTypeOptions}
                                                    items={dropdownItems}
                                                    onClick={dropdownClickHandler}
                                                />
                                            }
                                        >
                                            <div
                                                onClick={() => {
                                                    if (isItemsModalOpen !== false || disabled) return;
                                                    drillDownOnClick(item);

                                                    //mixpanel event
                                                    trackEventMixpanel(GROWTH_ANALYSIS_LEFT_CLICKED_DRILLDOWN, {
                                                        dimension: attributeTypeItem?.dimension,
                                                        attributeType: attributeTypeItem?.selectedAttributeType,
                                                        attributeName: item?.[0]?.category,
                                                    });
                                                }}
                                                style={{
                                                    position: 'relative',
                                                }}
                                                className={`${
                                                    !disabled || !disabledBarExploreMore
                                                        ? (isItemSelected
                                                              ? 'bg-bgBackground '
                                                              : 'hover:bg-greyBackgroundAlt ') +
                                                          (isItemsModalOpen !== false || disabled
                                                              ? ''
                                                              : 'cursor-pointer')
                                                        : ''
                                                } group`}
                                                onContextMenu={(e) => {
                                                    //allow right click for all levels - (even lower levels - prod, geo)
                                                    if (disableRightClick) return;
                                                    handleRightClick(e, idx);
                                                }}
                                            >
                                                {!disabledBarExploreMore && (
                                                    <div
                                                        style={{
                                                            position: 'absolute',
                                                            top: 5,
                                                            left: '50%',
                                                            transform: 'translateX(-50%)',
                                                            zIndex: 9,
                                                            paddingBottom: 80,
                                                            height: 20,
                                                            width: '100%',
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                position: 'absolute',
                                                                top: 5,
                                                                left: '50%',
                                                                transform: 'translateX(-50%)',
                                                            }}
                                                            className={`${
                                                                !disabledBarExploreMore
                                                                    ? 'group-hover:block cursor-pointer z-[9]'
                                                                    : ''
                                                            } `}
                                                            onClick={(e) => {
                                                                exploreMoreOnClick(item);
                                                                e.stopPropagation();

                                                                //mixpanel event
                                                                trackEventMixpanel(
                                                                    GROWTH_ANALYSIS_EXPLORE_MORE_CLICKED,
                                                                    {
                                                                        attributeName: item?.[0]?.category,
                                                                    },
                                                                );
                                                            }}
                                                        >
                                                            <Tooltip
                                                                placement="top"
                                                                color="white"
                                                                destroyTooltipOnHide
                                                                overlayStyle={{ borderRadius: 4 }}
                                                                overlayInnerStyle={{
                                                                    fontSize: graphXAxisLabelFontSize,
                                                                    color: colorConstants.darkGrey,
                                                                }}
                                                                title={
                                                                    expandedItem?.attributeExId === item[0].category
                                                                        ? 'Explore Less'
                                                                        : 'Explore More'
                                                                }
                                                            >
                                                                <div className="group">
                                                                    {expandedItem?.attributeExId ===
                                                                    item[0].category ? (
                                                                        expandMoreFillSvg
                                                                    ) : (
                                                                        <div className="hidden group-hover:block">
                                                                            {expandMoreSvg}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </Tooltip>
                                                        </div>
                                                    </div>
                                                )}
                                                <div style={{ height: height }}>
                                                    <Barchart
                                                        barWidth={barWidth}
                                                        //yAxisConfig={false}
                                                        dataset={item}
                                                        height={height}
                                                        loading={false}
                                                        chartContainer={`SalesX-${index}-${idx}`}
                                                        fields={['category', 'sales']}
                                                        intervalLabelTooltipKey={'labelSales'}
                                                        minBound={scaleMinValue(
                                                            absTile.key === 'percentage' ? 40 : 105,
                                                            salesData?.minVal,
                                                        )}
                                                        maxBound={scaleMaxValue(
                                                            absTile.key === 'percentage' ? 40 : 80,
                                                            salesData?.maxVal,
                                                        )}
                                                        width={perGraphComponentWidth || salesGraphWidth}
                                                        tooltipFormatter={(xAxis, val) => {
                                                            return {
                                                                name: xAxis,
                                                                value:
                                                                    absTile.key === 'percentage'
                                                                        ? currency +
                                                                          formatNumWithKMBAlt(
                                                                              val,
                                                                              decimalForPercentage,
                                                                          ) +
                                                                          '%'
                                                                        : currency +
                                                                          formatNumWithKMBAlt(
                                                                              val,
                                                                              decimalForNonPercentage,
                                                                              decimalForNonPercentage,
                                                                          ),
                                                            };
                                                        }}
                                                        intervalFormatter={(val) => {
                                                            return absTile.key === 'percentage'
                                                                ? currency +
                                                                      formatNumWithKMBAlt(val, decimalForPercentage) +
                                                                      '%'
                                                                : currency +
                                                                      formatNumWithKMBAlt(
                                                                          val,
                                                                          decimalForNonPercentage,
                                                                          decimalForNonPercentage,
                                                                      );
                                                        }}
                                                        //addPercentageInIntervalLabel={absTile.key === 'percentage'}
                                                        // xAxisLabelFormatter={(val: any) => {
                                                        //     return addLineAndFormatString(
                                                        //         val,
                                                        //         Math.floor(
                                                        //             (perGraphComponentWidth || salesGraphWidth) / 4.5,
                                                        //         ), //got 4.5 when salesGraphWidth default width is divided by 16 character
                                                        //     );
                                                        // }}
                                                        colorKey={'color'}
                                                    />
                                                </div>
                                                {/* {item?.length > 0 && (
                                                    <div
                                                        style={{}}
                                                        className="p-1 text-center text-[10px] text-greyFont twoLineEllipsis"
                                                    >
                                                        {item[0]?.category}
                                                    </div>
                                                )} */}
                                            </div>
                                        </Popover>
                                        <div
                                            style={{
                                                width: perGraphComponentWidth || salesGraphWidth,
                                                overflowWrap: 'anywhere',
                                                // wordBreak: 'break-word',
                                                fontSize: graphXAxisLabelFontSize,
                                            }}
                                            className="text-center p-[2px]"
                                        >
                                            {showFullLabel ? (
                                                item[0]?.category
                                            ) : (
                                                <Tooltip
                                                    placement="top"
                                                    color="white"
                                                    destroyTooltipOnHide
                                                    overlayStyle={{ borderRadius: 4 }}
                                                    overlayInnerStyle={{
                                                        fontSize: graphXAxisLabelFontSize,
                                                        color: colorConstants.darkGrey,
                                                    }}
                                                    title={item[0]?.category}
                                                >
                                                    <div>{ellipseMiddleString(item[0]?.category, 8, 3)}</div>
                                                </Tooltip>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                    </div>
                </HorizontalInfiniteScroller>
            </div>
        </section>
    );
});

export default SalesChartContainer;

type DropdownItemsType = {
    items: any[];
    item: any;
    onClick: (item: any, attrType: any, index: number) => void;
    attrTypeOptions: any;
};

export const DropdownItems = ({ item, items, onClick, attrTypeOptions }: DropdownItemsType) => {
    return (
        <div className="max-h-[200px] pr-1 overflow-auto">
            {items?.length > 0 ? (
                items.map((attrType: any) => {
                    return (
                        <div
                            onClick={() => {
                                onClick(item, attrType, attrTypeOptions.data?.length - items.length);
                            }}
                            className="p-1 text-purpleFont cursor-pointer hover:bg-buttonBgColor"
                            key={attrType?.value}
                        >
                            {attrType.label}
                        </div>
                    );
                })
            ) : (
                <div>No data</div>
            )}
        </div>
    );
};
